import { call, put, select, takeLatest } from 'redux-saga/effects';

import { userInfoSlice } from '!core/redux';
import { onboardingSlice } from '»components/Auth';
import { getActiveMenuItemSelector } from '»components/Menus';

import { surveyIdQuestionnaireSelector } from './questionnaire.selectors';
import { questionnaireServices } from './questionnaire.services';
import { questionnaireSlice } from './questionnaire.slice';

function transformSurveyData(data) {
  const surveyQuestions = data.filter(
    (item) => item.type === 'SurveyQuestions',
  );
  const responseOptions = data.filter(
    (item) => item.type === 'ResponseOptions',
  );

  const groupedOptions = responseOptions.reduce((acc, option) => {
    const { questionId, description, ...rest } = option;
    if (!acc[questionId]) acc[questionId] = [];
    acc[questionId].push({ ...rest, label: description });
    return acc;
  }, {});

  const transformedData = surveyQuestions.map((question) => {
    return {
      ...question,
      options: groupedOptions[question.questionId] || [],
    };
  });

  return transformedData;
}

const getQuestionnaireWorker = function* (action) {
  try {
    const { holderCode, openingRequestId } = action.payload;
    const questions = yield call(questionnaireServices.getQuestionnaires, {
      holderCode,
      openingRequestId,
    });
    const newData = transformSurveyData(questions);

    yield put(
      questionnaireSlice.actions.getQuestionnaireSuccess({
        questions: newData,
        initialText: questions[0]?.disclaimer,
        surveyId: questions[0]?.surveyId,
      }),
    );
  } catch (ex) {
    const { disableFormSubmittingState } = action.payload;

    yield put(questionnaireSlice.actions.getQuestionnaireFailure(ex));
    if (disableFormSubmittingState) disableFormSubmittingState();
  }
};

export const postQuestionnaireWorker = function* ({ payload }) {
  try {
    const { openingRequestId, holderCode, questValues } = payload;
    const surveyId = yield select(surveyIdQuestionnaireSelector);
    const activeMenuItem = yield select(getActiveMenuItemSelector);

    const entries = Object.keys(questValues)
      .map((answerId) => {
        const { groupId, value } = questValues[answerId];
        if (Array.isArray(value)) {
          return `<R><G>${groupId}</G><Q>${answerId}</Q>${value
            .map((v) => {
              return `<V>${v}</V>`;
            })
            .join('')}</R>`;
        }
        return `<R><G>${groupId}</G><Q>${answerId}</Q><V>${value}</V></R>`;
      })
      .join('');
    const newAnswers = `<RL>${entries}</RL>`;

    yield call(questionnaireServices.postSubmitQuestionnaire, {
      openingRequestId,
      holderCode,
      surveyId,
      answers: newAnswers,
    });

    yield put(
      questionnaireSlice.actions.submitQuestionnaireAnswersSuccess({
        answers: questValues,
      }),
    );

    if (activeMenuItem === 'ONBOARDING') {
      yield put(
        onboardingSlice.actions.generateContractPdfRequest({
          openingRequestId,
        }),
      );

      yield put(
        onboardingSlice.actions.setOnboardingCurrentStep({
          direction: 'forward',
        }),
      );
    } else {
      yield put(userInfoSlice.actions.userInfoRequest());
    }
  } catch (error) {
    yield put(
      questionnaireSlice.actions.submitQuestionnaireAnswersFailure(error),
    );
  }
};

export const questionnaireSagas = function* () {
  yield takeLatest(
    questionnaireSlice.actions.getQuestionnaireRequest.type,
    getQuestionnaireWorker,
  );
  yield takeLatest(
    questionnaireSlice.actions.submitQuestionnaireAnswersRequest.type,
    postQuestionnaireWorker,
  );
};
